// Shared design tokens for G3.8 subscription pages. Kept local so the Orio360
// glass aesthetic stays consistent across the three top-level pages and the
// tenant-detail subscription tab without re-importing private @bbpos/ui paths.


export const GLASS_PANEL =
  'rounded-2xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm shadow-[0_12px_30px_-18px_rgba(15,23,42,0.22)] dark:shadow-[0_12px_30px_-18px_rgba(0,0,0,0.5)]';

export const GLASS_FILTER =
  'rounded-2xl bg-white/60 dark:bg-neutral-900/60 backdrop-blur-sm shadow-[0_8px_24px_-20px_rgba(15,23,42,0.18)] dark:shadow-[0_8px_24px_-20px_rgba(0,0,0,0.4)]';

export function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

export type Tone = 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'neutral';

export function toneClasses(tone: Tone): { icon: string; accent: string; dot: string; pill: string } {
  switch (tone) {
    case 'success':
      return {
        icon: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        accent: 'from-emerald-500/20 to-emerald-500/0',
        dot: 'bg-emerald-500',
        pill: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
      };
    case 'warning':
      return {
        icon: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        accent: 'from-amber-500/18 to-amber-500/0',
        dot: 'bg-amber-500',
        pill: 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
      };
    case 'danger':
      return {
        icon: 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400',
        accent: 'from-rose-500/18 to-rose-500/0',
        dot: 'bg-rose-500',
        pill: 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
      };
    case 'info':
      return {
        icon: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
        accent: 'from-sky-500/18 to-sky-500/0',
        dot: 'bg-sky-500',
        pill: 'bg-sky-50 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
      };
    case 'neutral':
      return {
        icon: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300',
        accent: 'from-neutral-500/15 to-neutral-500/0',
        dot: 'bg-neutral-400',
        pill: 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300',
      };
    default:
      return {
        icon: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400',
        accent: 'from-indigo-500/18 to-indigo-500/0',
        dot: 'bg-indigo-500',
        pill: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
      };
  }
}

export type PlanTierKey = 'TRIAL' | 'BASIC' | 'PRO' | 'ENTERPRISE' | string;

/** Maps a plan slug to its visual tone. Unknown plans fall back to `brand`. */
export function planTone(name?: string | null): Tone {
  const key = (name ?? '').toUpperCase();
  if (key === 'TRIAL') return 'info';
  if (key === 'BASIC') return 'brand';
  if (key === 'PRO') return 'warning';
  if (key === 'ENTERPRISE') return 'success';
  return 'brand';
}

/**
 * Active/inactive colour coding for plan cards: green for active, red for
 * inactive. Returned tone doubles as the `<Badge variant>` so the card badge
 * and the card border can never drift apart.
 */
export function planStatusStyle(isActive: boolean): {
  tone: 'success' | 'danger';
  label: 'Active' | 'Inactive';
  border: string;
} {
  return isActive
    ? {
        tone: 'success',
        label: 'Active',
        border: 'border border-emerald-200 dark:border-emerald-800/60',
      }
    : {
        tone: 'danger',
        label: 'Inactive',
        border: 'border border-rose-300 dark:border-rose-800/60',
      };
}

export function invoiceStatusTone(status: string | null | undefined): Tone {
  switch (status) {
    case 'PAID':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'FAILED':
      return 'danger';
    case 'REFUNDED':
      return 'info';
    case 'CANCELLED':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function changeTypeTone(type: string | null | undefined): Tone {
  switch (type) {
    case 'UPGRADE':
      return 'success';
    case 'RENEWAL':
      return 'brand';
    case 'DOWNGRADE':
      return 'warning';
    case 'CANCELLATION':
    case 'TRIAL_END':
      return 'danger';
    case 'TRIAL_START':
      return 'info';
    default:
      return 'neutral';
  }
}

export function formatINRDate(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Whole days left until the subscription expires, floored at 0. Returns 0 when
 * there is no end date — callers render '—' in that case. Shared so the tenant
 * Overview and Subscription tabs never drift apart.
 */
export function subscriptionDaysRemaining(
  expiresAt?: string | null,
  now: number = Date.now(),
): number {
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - now) / 86400000));
}

export function formatINRDateTime(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

